import { useIsFetching } from '@tanstack/react-query';
import { useNavigation } from 'react-router-dom';
import React from 'react';

import { useAuthContext, useOrganizationContext } from '../../../context';

/** How long nothing may be in flight before the first screen counts as ready. */
const SETTLE_DELAY_MS = 300;
/** A query that never settles (a stalled request, a long poll) must not trap the user. */
const MAX_WAIT_MS = 10_000;

/**
 * MV2-596: after login the app walked through every intermediate state on
 * screen — the personal (client) profile while the organization was still
 * being resolved, then the organization's home with empty sections, then the
 * data. The page is a chain of dependent steps (organization → redirect to the
 * org home → lazy route module → its queries), and each one is a moment where
 * nothing is loading yet but nothing is right either.
 *
 * So the first screen after a sign-in stays behind one loader until the whole
 * chain has gone quiet: the organization is resolved, no query is fetching and
 * no navigation is pending, for `SETTLE_DELAY_MS` in a row. It releases once
 * per account — later fetches (paging, filters, an org switch) keep their own
 * in-page loading states.
 */
export const useInitialLoadGate = () => {
  const { currentUser } = useAuthContext();
  const { isLoading: isOrganizationLoading } = useOrganizationContext();
  const fetchingCount = useIsFetching();
  const navigation = useNavigation();

  const accountId = currentUser?.id;
  const [releasedFor, setReleasedFor] = React.useState<string | undefined>();
  const isReleased = !accountId || releasedFor === accountId;

  const isBusy =
    isOrganizationLoading || fetchingCount > 0 || navigation.state !== 'idle';

  React.useEffect(() => {
    if (isReleased || isBusy) return;
    const timeout = setTimeout(
      () => setReleasedFor(accountId),
      SETTLE_DELAY_MS,
    );
    return () => clearTimeout(timeout);
  }, [accountId, isBusy, isReleased]);

  React.useEffect(() => {
    if (isReleased) return;
    const timeout = setTimeout(() => setReleasedFor(accountId), MAX_WAIT_MS);
    return () => clearTimeout(timeout);
  }, [accountId, isReleased]);

  return {
    /** The organization is not known yet — anything rendered would be the wrong profile. */
    isOrganizationLoading,
    isInitialLoad: !isReleased,
  };
};
